import { User } from 'firebase/auth';
import { firestore } from '../configs/firebase';
import {
  collection,
  doc,
  getDoc,
  getDocs,
  query,
  where,
  deleteDoc,
  writeBatch,
  DocumentData,
} from 'firebase/firestore';
import { chunkArray } from '@leedpulse-monorepo/utils';
import { JobType, UserType } from '@leedpulse-monorepo/enums';

export const getJobsByContactId = async (user: User, contactId: string) => {
  try {
    const token = await user.getIdTokenResult();
    const keys = token.claims.api_key;

    const jobsRef = collection(firestore, 'Jobs');
    const q = query(
      jobsRef,
      where('id_contact', '==', contactId),
      where('id_api_key', 'in', keys)
    );
    const querySnapshot = await getDocs(q);

    const aJobs: DocumentData[] = [];
    // A Leedflow contact's workflow jobs are not SMS: never listed
    querySnapshot?.docs.filter((job) => job.data().type !== JobType.workflow).forEach((job) => {
      const oJob = job.data();
      oJob.id_doc = job.id;
      aJobs.push(oJob);
    });

    return {
      success: true,
      status: 'success',
      msg: 'Liste des jobs récupérée avec succès.',
      doc: aJobs,
    };
  } catch (error) {
    return {
      success: false,
      status: 'error',
      msg: (error as Error).message.toLocaleString(),
    };
  }
}

export const getJobsByPeriod = async (userData: DocumentData, sTimestampBegin: number, sTimestampEnd: number, apiKey: string) => {
  try {
    const jobsRef = collection(firestore, 'Jobs');
    const jobsQuery = query(
      jobsRef,
      where('id_api_key', '==', apiKey),
      where('timestamp', '>=', sTimestampBegin),
      where('timestamp', '<=', sTimestampEnd)
    );
    const jobsSnapshot = await getDocs(jobsQuery);

    // A Leedflow contact's workflow jobs are not SMS: never planned
    const jobsDocs = jobsSnapshot.docs
      .filter((jobDoc) => jobDoc.data().type !== JobType.workflow)
      .map((jobDoc): DocumentData => ({ ...jobDoc.data(), id_doc: jobDoc.id }));

    const contactsRef = collection(firestore, 'Contacts');
    const contactIds = [...new Set(jobsDocs.map((jobData) => jobData.id_contact).filter(Boolean))];

    // Batch-fetch the unique contacts (`in` supports up to 30 ids) instead of
    // running one query per job.
    const contactChunks = chunkArray(contactIds, 30);

    const contactsById = new Map<string, DocumentData>();
    await Promise.all(
      contactChunks.map(async (chunk) => {
        const contactConstraints = [where('id', 'in', chunk)];

        if (userData?.type === UserType.USER) {
          contactConstraints.push(where('contact.owner', '==', userData.uid));
        }

        const contactSnapshot = await getDocs(query(contactsRef, ...contactConstraints));
        contactSnapshot.docs.forEach((contactDoc) => {
          const contactData = contactDoc.data();
          contactsById.set(contactData.id, contactData);
        });
      })
    );

    const jobsWithContactsFiltered = jobsDocs
      .map((jobData) => {
        const contact = contactsById.get(jobData.id_contact);
        return contact ? { ...jobData, contact } : null;
      })
      .filter((job) => job !== null);

    return {
      success: true,
      status: 'success',
      msg: 'Liste des jobs récupérée avec succès.',
      doc: jobsWithContactsFiltered,
    };
  } catch (error) {
    return {
      success: false,
      status: 'error',
      msg: (error as Error).message.toString(),
    };
  }
}

export const deleteFollowUpNotifications = async (contactId: string) => {
  try {
    const notificationsRef = collection(firestore, 'Notifications');
    const q = query(notificationsRef, where('id_contact', '==', contactId));
    const querySnapshot = await getDocs(q);

    if (querySnapshot.empty) {
      console.log('Aucune notification trouvée pour ce contact');
      return;
    }

    const batch = writeBatch(firestore);

    querySnapshot.forEach((docSnapshot) => {
      const notificationType = docSnapshot.data().type;
      if (
        notificationType &&
        notificationType.includes('follow_up_') &&
        notificationType.includes('_days')
      ) {
        batch.delete(docSnapshot.ref);
      }
    });

    await batch.commit();
    console.log('Notifications de relance supprimées avec succès');
  } catch (error) {
    console.error('Erreur lors de la suppression des notifications:', error);
  }
}

export const deleteJobById = async (jobId: string) => {
  try {
    const jobRef = doc(firestore, 'Jobs', jobId);
    const jobDoc = await getDoc(jobRef);

    if (!jobDoc.exists()) {
      return {
        success: false,
        status: 'error',
        msg: "Le job n'existe pas.",
      };
    }

    const jobData = jobDoc.data();

    if (
      jobData.type &&
      jobData.type.includes('follow_up_') &&
      jobData.type.includes('_days')
    ) {
      await deleteFollowUpNotifications(jobData.id_contact);
    }

    await deleteDoc(jobRef);

    return {
      success: true,
      status: 'success',
      msg: 'Le job et les notifications associées ont été supprimés avec succès.',
    };
  } catch (error) {
    console.error('Erreur lors de la suppression du job:', error);
    return {
      success: false,
      status: 'error',
      msg: (error as Error).message,
    };
  }
}

export default {
  getJobsByContactId,
  getJobsByPeriod,
  deleteFollowUpNotifications,
  deleteJobById,
};